import React, { useEffect, useState } from "react";
import {
  useContactHistoryQuery,
  useOldestcontactHistoryWithAccountQuery,
} from "../../../Services/customer/FrontendCustomer";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Divider, Pagination, PaginationItem } from "@mui/material";
import { formatDate } from "../../reusableComponent/DateFormat";
import LoadingSpinner from "../../LoadingSpinner";
import {
  getBadgeClasses,
  getIconClasses,
} from "../../reusableComponent/IconAndBadgeClasses";
import "../../email/mail/MailHistory.scss";

interface IPROPS {}

function getTimelineDotProps(item: any): {
  className: string;
  iconClass: string;
  style?: React.CSSProperties;
} {
  const action = item?.action_type;
  if (action === "email") {
    return {
      className: `timeline-dot ${getBadgeClasses("email")}`,
      iconClass: getIconClasses("email"),
    };
  }
  if (action === "ticket") {
    return {
      className: "timeline-dot rounded-circle d-flex align-items-center justify-content-center",
      style: { backgroundColor: "#8b5cf6", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" },
      iconClass: "bi bi-ticket-perforated-fill fs-6 text-white",
    };
  }
  if (action === "telephony") {
    const k = item?.call_type === "outbound_phone" ? "outbound_phone" : "inbound_phone";
    return {
      className: `timeline-dot ${getBadgeClasses(k)}`,
      iconClass: getIconClasses(k),
    };
  }
  return {
    className: `timeline-dot ${getBadgeClasses("Email")}`,
    iconClass: getIconClasses("Email"),
  };
}

function getHistoryChannelLabel(item: any): string {
  if (item?.action_type === "email") return "Email";
  if (item?.action_type === "telephony") {
    const raw = item?.call_type || "Phone";
    return String(raw).replace(/_/g, " ");
  }
  return item?.action_type || "Ticket";
}

function getHistoryTimestamp(item: any): string | undefined {
  if (item?.action_type === "telephony") return item?.created;
  return item?.created || item?.contact?.created || item?.ticket?.created;
}

function EmailThreadContent({ thread }: { thread: any }) {
  if (!thread) return null;
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Subject
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        {thread?.subject ?? "—"}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {(thread?.emails ?? []).map((email: any) => (
        <Box key={email?.id} sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              bgcolor: "grey.100",
              p: 1.5,
              gap: 1,
            }}
          >
            <Typography variant="body2" component="div">
              <span className="text-muted">To: {email.to}</span>
              {email?.cc?.length > 0 && (
                <>
                  <br />
                  <span className="text-muted">cc: {email.cc}</span>
                </>
              )}
              {email?.bcc?.length > 0 && (
                <>
                  <br />
                  <span className="text-muted">bcc: {email.bcc}</span>
                </>
              )}
            </Typography>
            <Typography variant="body2" noWrap>
              {formatDate(email?.mail_sent_or_received_on)}
            </Typography>
          </Box>
          <Box className="py-3 p-2" dangerouslySetInnerHTML={{ __html: email.html_content }} />
          {email?.attachments?.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Attachments
              </Typography>
              {email?.attachments?.map((file: any) => (
                <Typography
                  key={file?.id}
                  component="span"
                  className="badge bg-secondary p-2"
                  sx={{ mr: 1, display: "inline-block", cursor: "pointer" }}
                >
                  {file?.file_name}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </>
  );
}

function HistoryRowDetailPanel({ drawerHistory }: { drawerHistory: any }) {
  if (drawerHistory?.action_type === "email") {
    const createdRaw =
      drawerHistory?.created ||
      drawerHistory?.contact?.created ||
      drawerHistory?.ticket?.created;
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Email
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Ref:</strong> {drawerHistory?.ref_id || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Created:</strong>{" "}
          {createdRaw ? formatDate(createdRaw) : "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Assignee:</strong>{" "}
          {drawerHistory?.assignee?.username?.split("@")[0] ?? "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Customer:</strong> {drawerHistory?.contact?.name || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Email:</strong> {drawerHistory?.contact?.email || "N/A"}
        </Typography>
        {drawerHistory?.ticket?.id != null && drawerHistory?.ticket?.id !== "" && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Ticket:</strong> #{drawerHistory.ticket.id}
          </Typography>
        )}
        {drawerHistory?.ticket?.subject != null && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Ticket subject:</strong> {String(drawerHistory.ticket.subject)}
          </Typography>
        )}
        {drawerHistory?.message != null && drawerHistory?.message !== "" && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Message:</strong> {String(drawerHistory.message)}
          </Typography>
        )}
        {drawerHistory?.thread ? (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Thread
            </Typography>
            <EmailThreadContent thread={drawerHistory.thread} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No email thread attached to this entry.
          </Typography>
        )}
      </>
    );
  }

  if (drawerHistory?.action_type === "ticket") {
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Ticket #{drawerHistory?.ticket?.id ?? drawerHistory?.ref_id ?? "—"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Priority:</strong> {drawerHistory?.ticket?.priority ?? "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Created:</strong>{" "}
          {drawerHistory?.ticket?.created ? formatDate(drawerHistory.ticket.created) : "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Customer:</strong> {drawerHistory?.contact?.email ?? "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Assignee:</strong> {drawerHistory?.assignee?.username?.split("@")[0] ?? "N/A"}
        </Typography>
        {drawerHistory?.ticket?.subject != null && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Subject:</strong> {String(drawerHistory.ticket.subject)}
          </Typography>
        )}
        {drawerHistory?.ticket?.description != null && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {String(drawerHistory.ticket.description)}
            </Typography>
          </Box>
        )}
      </>
    );
  }

  if (
    drawerHistory?.action_type === "telephony" ||
    (drawerHistory?.action_type !== "email" &&
      (drawerHistory?.recording_link || drawerHistory?.dispose_remark))
  ) {
    return (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Ref:</strong> {drawerHistory?.ref_id || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Call Type:</strong> {drawerHistory?.call_type || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Agent:</strong> {drawerHistory?.assignee?.first_name}{" "}
          {drawerHistory?.assignee?.last_name || ""}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Customer:</strong> {drawerHistory?.contact?.name || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Email:</strong> {drawerHistory?.contact?.email || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Disposition:</strong> {drawerHistory?.disposition?.priority || "N/A"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Dispose Remark:</strong> {drawerHistory?.dispose_remark || "N/A"}
        </Typography>
        {drawerHistory?.recording_link && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Recording
            </Typography>
            <audio controls style={{ width: "100%" }}>
              <source src={drawerHistory.recording_link} type="audio/wav" />
              Your browser does not support the audio element.
            </audio>
          </Box>
        )}
      </>
    );
  }

  if (drawerHistory?.thread) {
    return <EmailThreadContent thread={drawerHistory.thread} />;
  }

  return (
    <Typography variant="body2" color="text.secondary">
      No extra details for this entry.
    </Typography>
  );
}

const CustomerHistory: React.FC<IPROPS> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [category, setCategory] = useState("newest");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { data: contactHistoryData, status: contactHistoryStatus } =
    useContactHistoryQuery({ id: id, page: currentPage }, { skip: category !== "newest" });
  const { data: oldestContactHistoryData, status: oldestContactHistoryStatus } =
    useOldestcontactHistoryWithAccountQuery(
      { id: id, page: currentPage },
      { skip: category !== "oldest" },
    );

  useEffect(() => {
    if (category === "oldest" && oldestContactHistoryData) {
      setHistoryData(oldestContactHistoryData.results || oldestContactHistoryData || []);
      setTotalPages(Math.ceil((oldestContactHistoryData.count || 0) / 10));
    } else if (category !== "oldest" && contactHistoryData) {
      setHistoryData(contactHistoryData.results || contactHistoryData || []);
      setTotalPages(Math.ceil((contactHistoryData.count || 0) / 10));
    }
  }, [category, contactHistoryData, oldestContactHistoryData]);

  useEffect(() => {
    if (contactHistoryStatus === "pending" || oldestContactHistoryStatus === "pending") {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [contactHistoryStatus, oldestContactHistoryStatus]);

  const formatDateFull = (timestamp: string) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getShortDate = (timestamp: string | undefined) => {
    if (!timestamp) return "N/A";

    const date = new Date(timestamp);
    const now = new Date();

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = nowOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays > 0 && diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const categoryChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleRowNavigate = (item: any) => {
    const ticketId = item?.ticket?.id;
    if (ticketId != null && ticketId !== "") {
      navigate(`/ticket/information/${ticketId}`);
    }
  };

  const handleTicketIdClick = (e: React.MouseEvent, ticketId: string | number) => {
    e.stopPropagation();
    navigate(`/ticket/information/${ticketId}`);
  };

  if (isLoading) {
    return (
      <div className="text-center py-3">
        <LoadingSpinner />
      </div>
    );
  }

  if (!historyData?.length) {
    return (
      <div className="history-empty-state">
        <div className="empty-icon-circle">
          <i className="bi bi-clock-history"></i>
        </div>
        <p className="empty-text">No history available</p>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: "10px",
        }}
      >
        <span>Sort by: </span>
        <span style={{ width: "30%", maxWidth: 220 }}>
          <select
            onChange={categoryChangeHandler}
            value={category}
            className="form form-select"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </span>
      </div>

      <div className="channel-history-modern" style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <div className="history-timeline">
          {historyData.map((item: any, index: number) => {
            const dot = getTimelineDotProps(item);
            const ts = getHistoryTimestamp(item);
            const ticketId = item?.ticket?.id;
            const hasTicketId = ticketId != null && ticketId !== "";

            return (
              <div
                key={item?.id ?? index}
                className={`timeline-item ${hoveredIndex === index ? "hovered" : ""}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => handleRowNavigate(item)}
              >
                <div className="timeline-track">
                  <div className={dot.className} style={dot.style}>
                    <i className={dot.iconClass}></i>
                  </div>
                  {index !== historyData.length - 1 && <div className="timeline-line"></div>}
                </div>

                <div className="timeline-card">
                  <div className="card-quick-info">
                    <div className="info-left">
                      <span className="channel-type">{getHistoryChannelLabel(item)}</span>
                      <span className="separator">•</span>
                      <span className="time-short">{getShortDate(ts)}</span>
                    </div>
                    {hasTicketId ? (
                      <div className="info-right">
                        <span
                          role="link"
                          tabIndex={0}
                          className="ticket-id"
                          onClick={(e) => handleTicketIdClick(e, ticketId)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/ticket/information/${ticketId}`);
                            }
                          }}
                        >
                          #{ticketId}
                        </span>
                        <i className="bi bi-chevron-right arrow-icon"></i>
                      </div>
                    ) : null}
                  </div>

                  <div
                    className="card-expandable-content"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item?.message && (
                      <div className="content-message">
                        <p>{item.message}</p>
                      </div>
                    )}

                    <Box sx={{ pt: 1 }}>
                      <HistoryRowDetailPanel drawerHistory={item} />
                    </Box>

                    <div className="content-footer">
                      <div className="footer-item">
                        <i className="bi bi-calendar3"></i>
                        <span>{formatDateFull(ts || "")}</span>
                      </div>
                      {(item?.assignee?.username || item?.assignee?.first_name) && (
                        <div className="footer-item assignee">
                          <i className="bi bi-person"></i>
                          <span>
                            {item?.action_type === "telephony"
                              ? [item?.assignee?.first_name, item?.assignee?.last_name]
                                  .filter(Boolean)
                                  .join(" ")
                              : item?.assignee?.username?.split("@")[0]}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(category === "newest" || category === "oldest") && (
        <div
          className="d-flex flex-row-reverse mt-4 mb-4"
          style={{ marginRight: "20px" }}
        >
          <Pagination
            sx={{
              display: "flex",
              justifyContent: "end",
              "& .MuiPaginationItem-root": {
                fontSize: "14px",
                minWidth: "36px",
                height: "36px",
                margin: "0 2px",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                color: "#374151",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  borderColor: "#d1d5db",
                },
                "&.Mui-selected": {
                  backgroundColor: "#6366f1",
                  color: "#ffffff",
                  borderColor: "#6366f1",
                  "&:hover": {
                    backgroundColor: "#4f46e5",
                    borderColor: "#4f46e5",
                  },
                },
              },
            }}
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            renderItem={(item) => {
              if (item.type === "page") {
                const isNearCurrent =
                  Math.abs(item.page - currentPage) <= 1 ||
                  item.page === 1 ||
                  item.page === totalPages;
                return isNearCurrent ? <PaginationItem {...item} /> : null;
              }
              return <PaginationItem {...item} />;
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerHistory;